
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, User, Building2 } from 'lucide-react';
import { OnboardingStepProps } from './types';
import { useOnboardingDataHydration } from '@/hooks/useOnboardingDataHydration';
import { useCreateOrganizationAtomic } from '@/hooks/useCreateOrganizationAtomic';
import { toast } from 'sonner';

export interface ProfileAndOrganizationSetupStepProps extends OnboardingStepProps {}

export const ProfileAndOrganizationSetupStep: React.FC<ProfileAndOrganizationSetupStepProps> = ({
  onComplete,
  onSkip,
  isLoading = false
}) => {
  const {
    userFormData,
    organizationFormData,
    setUserFormData,
    setOrganizationFormData,
    hasExistingOrganization
  } = useOnboardingDataHydration();

  const createOrganizationMutation = useCreateOrganizationAtomic();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleUserFieldChange = (field: string, value: string) => {
    setUserFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleOrgFieldChange = (field: string, value: string) => {
    setOrganizationFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!userFormData.firstName || !userFormData.lastName) {
      toast.error('Please fill in your first and last name');
      return;
    }

    if (!hasExistingOrganization && !organizationFormData.organizationName) {
      toast.error('Please enter your organization name');
      return;
    }

    setIsSubmitting(true);

    try {
      if (!hasExistingOrganization) {
        await createOrganizationMutation.mutateAsync({
          organizationName: organizationFormData.organizationName,
          industry: organizationFormData.industry || null,
          companySize: organizationFormData.companySize || null,
          location: organizationFormData.location || null,
          profileData: {
            firstName: userFormData.firstName,
            lastName: userFormData.lastName,
            jobTitle: userFormData.jobTitle,
            experienceLevel: userFormData.experienceLevel,
            goals: userFormData.goals
          }
        });
      }

      await onComplete();
    } catch (error) {
      console.error('Setup error:', error);
      toast.error('Failed to complete setup');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold">Complete Your Profile</h2>
        <p className="text-muted-foreground">
          Let's set up your profile and organization to get you started
        </p>
      </div>

      <div className="grid gap-6">
        {/* User Profile Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Personal Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName">First Name *</Label>
                <Input
                  id="firstName"
                  value={userFormData.firstName}
                  onChange={(e) => handleUserFieldChange('firstName', e.target.value)}
                  placeholder="Enter your first name"
                />
              </div>
              <div>
                <Label htmlFor="lastName">Last Name *</Label>
                <Input
                  id="lastName"
                  value={userFormData.lastName}
                  onChange={(e) => handleUserFieldChange('lastName', e.target.value)}
                  placeholder="Enter your last name"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="jobTitle">Job Title</Label>
              <Input
                id="jobTitle"
                value={userFormData.jobTitle}
                onChange={(e) => handleUserFieldChange('jobTitle', e.target.value)}
                placeholder="e.g., Senior Recruiter"
              />
            </div>

            <div>
              <Label htmlFor="experienceLevel">Experience Level</Label>
              <Select
                value={userFormData.experienceLevel}
                onValueChange={(value) => handleUserFieldChange('experienceLevel', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select your experience level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="entry">Entry Level (0-2 years)</SelectItem>
                  <SelectItem value="mid">Mid Level (3-5 years)</SelectItem>
                  <SelectItem value="senior">Senior Level (6-10 years)</SelectItem>
                  <SelectItem value="expert">Expert Level (10+ years)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="goals">Goals</Label>
              <Textarea
                id="goals"
                value={userFormData.goals}
                onChange={(e) => handleUserFieldChange('goals', e.target.value)}
                placeholder="What are your recruiting goals?"
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Organization Section */}
        {!hasExistingOrganization && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Organization Setup
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="organizationName">Organization Name *</Label>
                <Input
                  id="organizationName"
                  value={organizationFormData.organizationName}
                  onChange={(e) => handleOrgFieldChange('organizationName', e.target.value)}
                  placeholder="Enter your organization name"
                />
              </div>

              <div>
                <Label htmlFor="industry">Industry</Label>
                <Select
                  value={organizationFormData.industry}
                  onValueChange={(value) => handleOrgFieldChange('industry', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select your industry" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="technology">Technology</SelectItem>
                    <SelectItem value="healthcare">Healthcare</SelectItem>
                    <SelectItem value="finance">Finance</SelectItem>
                    <SelectItem value="consulting">Consulting</SelectItem>
                    <SelectItem value="manufacturing">Manufacturing</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="companySize">Company Size</Label>
                <Select
                  value={organizationFormData.companySize}
                  onValueChange={(value) => handleOrgFieldChange('companySize', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select company size" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1-10">1-10 employees</SelectItem>
                    <SelectItem value="11-50">11-50 employees</SelectItem>
                    <SelectItem value="51-200">51-200 employees</SelectItem>
                    <SelectItem value="201-1000">201-1000 employees</SelectItem>
                    <SelectItem value="1000+">1000+ employees</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="location">Location</Label>
                <Input
                  id="location"
                  value={organizationFormData.location}
                  onChange={(e) => handleOrgFieldChange('location', e.target.value)}
                  placeholder="e.g., San Francisco, CA"
                />
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <div className="flex gap-3">
        {onSkip && (
          <Button variant="outline" onClick={onSkip} disabled={isLoading || isSubmitting}>
            Skip for now
          </Button>
        )}
        <Button 
          onClick={handleSubmit} 
          disabled={isLoading || isSubmitting} 
          className="flex-1"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Setting up...
            </>
          ) : (
            'Continue'
          )}
        </Button>
      </div>
    </div>
  );
};
